/**
 * Where a number came from — the single most important thing on the facts
 * surface. The brief: "a reader has to be able to tell at a glance which
 * numbers a machine guessed and which came from the form."
 *
 * Three rules hold this component together (the table itself lives in
 * `components/provenance.ts`, where the tests can reach it):
 *
 * 1. **Never colour-only.** Every badge renders its own Lucide glyph AND the
 *    word. Five provenances, five different glyphs, five different words, three
 *    different border styles — the distinction survives greyscale, print and a
 *    colour-blind reader.
 * 2. **Never a traffic light.** Not one of these badges may use a signal token
 *    (`signal-red/amber/green`). Provenance is not severity: "this value came
 *    from a model" is not "this rule is violated", and a reader who learns to
 *    read one palette as the other has been taught something false.
 * 3. **`unbekannt` is its own state, not a default.** A value that arrived
 *    without provenance says so.
 */
import { Icon } from '@/components/Icon'
import { PROVENANCE_LOOK } from '@/components/provenance'
import { useI18n } from '@/i18n'
import { cn } from '@/lib/utils'
import type { Provenance } from '@/api/types'

export function ProvenanceBadge({
  provenance,
  className,
}: {
  provenance: Provenance
  className?: string
}) {
  const { t } = useI18n()
  // A provenance this build has never heard of degrades to "unbekannt" rather
  // than to a blank badge — the row still says where it does not come from.
  const look = PROVENANCE_LOOK[provenance] ?? PROVENANCE_LOOK.unbekannt

  return (
    <span
      data-testid="provenance-badge"
      data-provenance={provenance}
      title={t(look.meaning)}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-pill border px-2.5 py-0.5 text-[12px] leading-5 font-medium',
        look.className,
        className,
      )}
    >
      <Icon icon={look.icon} className="size-3.5" />
      <span>{t(look.label)}</span>
    </span>
  )
}

/**
 * Confidence, as the engine delivered it — `hoch | mittel | tief`.
 *
 * A three-step meter plus the word. The meter is decorative (`aria-hidden`):
 * the word is the accessible content, so nothing here depends on shape or
 * colour alone. An absent confidence renders NOTHING — not "—", not "0", not
 * "unbekannt". Null over guess applies to the metadata too.
 */
const STEPS: Record<string, number> = { hoch: 3, high: 3, mittel: 2, medium: 2, tief: 1, low: 1 }

export function ConfidenceMeter({ confidence }: { confidence?: string | null }) {
  const { t } = useI18n()
  if (!confidence) return null
  const filled = STEPS[confidence.toLowerCase()] ?? 0

  return (
    <span
      data-testid="confidence"
      data-confidence={confidence}
      className="inline-flex items-center gap-1.5 text-[12px] text-muted"
    >
      <span aria-hidden="true" className="inline-flex items-end gap-0.5">
        {[1, 2, 3].map((step) => (
          <span
            key={step}
            className={cn(
              'w-1 rounded-xs',
              step === 1 ? 'h-1.5' : step === 2 ? 'h-2.5' : 'h-3.5',
              step <= filled ? 'bg-ink-2' : 'bg-border',
            )}
          />
        ))}
      </span>
      <span>
        {t('facts.confidence')}: {confidence}
      </span>
    </span>
  )
}
